import {
    Component,
    Input,
    Output,
    EventEmitter,
    ViewChild,
    ElementRef,
    forwardRef, Renderer2, AfterViewInit,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

const DISPLAY_PRECISION: number = 8;

@Component({
    selector: 'number-input',
    template: `
        <input #inputField
               class="field number-field"
               type="number"
               [id]="id"
               [class.alignBottom]="isAlignBottom"
               [class.full]="isFull"
               [style.textAlign]="align"
               [attr.min]="min"
               [attr.max]="max"
               [attr.step]="step"
               (focus)="onFocusEvent($event)"
               (keyup)="onKeyUp.emit($event)"
               (keydown)="onKeyDownEvent($event)"
               (blur)="touch(); onBlur.emit($event)"
               [tabindex]="tabindex"
               (click)="onClick.emit($event);"
               [value]="value"
               (change)="onChangeEvent($event)"
               [placeholder]="placeholder || ''">
    `,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => NumberInputComponent),
            multi: true,
        }
    ],
    styles: [`
        :host {
            display: inline-block;
        }

        input {
            -moz-appearance: textfield;
            appearance: textfield;
            margin: 0;
        }

        input::-webkit-inner-spin-button,
        input::-webkit-outer-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        input.full{
            height: 100%;
            border:0;
        }

        input.field.number-field.alignBottom {
            top: auto;
        }
    `]
})
/**
 * Low-level number component to be used inside a number field
 * /!\ FixedDigits default value is 2: numbers will be formated with 2 digits after decimal separator
 *
 * To force any other format, you can pass another value or **null** if numbers should not be formatted
 */
export class NumberInputComponent implements ControlValueAccessor, AfterViewInit {
    @Input()
    public id: string;
    @Input()
    public disabled: boolean;
    @Input()
    public tabindex: number;
    @Input()
    public placeholder: string;
    @Input()
    public min: number;
    @Input()
    public max: number;
    @Input()
    public step: number;
    @Input()
    public isFull: boolean;
    @Input()
    public isAlignBottom: boolean;
    @Input()
    public align: "center" | "left" | "right" = "right";
    @Input()
    public fixedDigits: number = 2;
    @Input()
    public allowKeyboardArrowsToChangeValues: boolean = true;

    @Output()
    public readonly onKeyUp: EventEmitter<KeyboardEvent> = new EventEmitter();
    @Output()
    public readonly onKeyDown: EventEmitter<KeyboardEvent> = new EventEmitter();
    @Output()
    public readonly onBlur: EventEmitter<UIEvent> = new EventEmitter();
    @Output()
    public readonly onClick: EventEmitter<MouseEvent> = new EventEmitter();
    @Output()
    public readonly onChange: EventEmitter<number> = new EventEmitter();
    @Output()
    public readonly onFocus: EventEmitter<UIEvent> = new EventEmitter();

    @ViewChild('inputField')
    public inputField: ElementRef;

    public value: string;
    private numberValue: number;

    private onChangeCallback: (value: number) => void;
    private onTouchedCallback?: () => void;

    constructor(private _renderer: Renderer2, private _elementRef: ElementRef){
    }

    public ngAfterViewInit(): void {
        this._renderer.removeAttribute(this._elementRef.nativeElement, 'id')
    }

    public onChangeEvent(event: Event): void {
        this.change(this.inputField.nativeElement.value);
    }

    public focus(): void {
        if (document.activeElement !== this.inputField.nativeElement) {
            this.inputField.nativeElement.focus();
        }
    }

    public onFocusEvent(event: UIEvent): void {
        this.inputField.nativeElement.select();
        this.onFocus.emit(event);
    }

    public blur(): void {
        if (document.activeElement === this.inputField.nativeElement) {
            this.inputField.nativeElement.blur();
        }
    }

    ////////////// Interface ControlValueAccessor
    public writeValue(value: number | string): void {
        this.value = cleanValue(value, this.fixedDigits);
        this.change(this.value);
    }

    // Allows Angular to register a function to call when the model (rating) changes.
    // Save the function as a property to call later here.
    public registerOnChange(fn: (values: number) => void): void {
        this.onChangeCallback = fn;
    }

    // Allows Angular to register a function to call when the input has been touched.
    // Save the function as a property to call later here.
    public registerOnTouched(fn: () => void): void {
        this.onTouchedCallback = fn;
    }

    ////////////// End Interface ControlValueAccessor

    public change(value: string): void {
        const next: number = toNumber(value);
        if (this.onChangeCallback && this.numberValue !== next) {
            this.numberValue = value ? next : null;
            this.onChangeCallback(this.numberValue);
            this.onChange.emit(this.numberValue);
        }
    }

    public touch(): void {
        if (this.onTouchedCallback) {
            this.onTouchedCallback();
        }

    }

    public onKeyDownEvent(event: KeyboardEvent): void {
        const ARROW_KEYS: string[] = ["ArrowUp", "ArrowDown"];

        if (!this.allowKeyboardArrowsToChangeValues && ARROW_KEYS.includes(event.key)) {
            event.preventDefault();
        }

        this.onKeyDown.emit(event);
    }

    public getCurrentValue(): number {
        return +this.inputField.nativeElement.value;
    }
}

function cleanValue(value: number | string, fixedDigits: number): string {
    if (isNaN(+value) || value === undefined || value === null) {
        return "";
    }

    const numberValue: number = +value;

    if (fixedDigits === undefined) {
        return numberValue.toFixed(2);
    }

    if (fixedDigits !== null) {
        return numberValue.toFixed(fixedDigits);
    }

    // Keep 6 significant digits
    // Because 0.1003 has the good idea to be rendered as 0.1003000000001
    // Thanks to float numbers representation in memory

    const representation: string = numberValue.toPrecision(DISPLAY_PRECISION)
        .replace(/\.?0*$/, "");

    return representation;
}

function toNumber(value: string): number {
    // Possible issue: english format with thousands separator
    return +((value || "").trim()
        .replace(",", "."));
}
